import { createColumnHelper } from "@tanstack/react-table";
import { Integration, Order, Setting } from "/app/src/models";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import { orderService, settingService } from "/app/src/services";
import useQueryParams from "/app/src/hooks/useQueryParams";
import { ArrowCircleRight } from "/app/src/components/icons/icons";
import InnerControls from "./_innerControls";
import Table from "/app/src/components/generic/tables/table";
import { useQuery } from "@tanstack/react-query";
import { buildParams } from "/app/src/helpers/params";

/**
 * Renders a list of orders for a specific integration with table controls and pagination.
 *
 * @param {object} props - The component props.
 * @param {Integration} props.integration - The integration object containing appId and id.
 * @returns {JSX.Element} The rendered orders table component.
 */
export default function OrderProcessorOrders({
  integration,
}: {
  integration: Integration;
}) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const columnHelper = createColumnHelper<Order>();
  const navigate = useNavigate();

  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );
  const handleSearch = useCallback((value) => {
    setSearchString(value);
  }, []);
  const [sort, setSort] = useSortUpgrade([]);

  /**
   * Parses a query string into a key/value object.
   *
   * @param queryString the query string to parse
   * @returns an object mapping keys to values from the query string
   */
  const parseQueryString = (queryString: string): Record<string, string> => {
    return queryString.split("&").reduce(
      (acc, pair) => {
        const [key, value] = pair.split("=");
        if (key) {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, string>,
    );
  };

  const { data: orderFilterData } = useQuery({
    queryKey: ["orderFilterData", integration.id],
    queryFn: () =>
      settingService.getAll(
        buildParams({
          integrationId: integration.id,
          type: "orderFilters",
        }),
      ),
    initialData: { settings: [] },
    select: (data: { settings: Setting[] }) => {
      return data.settings[0]?.value;
    },
  });

  // Parse orderFilterData into key/value pairs
  const filterOptions = parseQueryString(orderFilterData || "");

  const {
    data: orders,
    count: ordersCount,
    isFetching,
  } = usePaginatedData<Order>({
    queryKey: ["orders"],
    searchString,
    sort,
    page,
    pageSize,
    service: orderService,
    options: {
      ...filterOptions,
    },
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: ordersCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  const columns = useMemo(() => {
    /*
     * Returns function that handles click event
     */
    const handleClick = (id: string) => {
      return () =>
        navigate(
          `/apps/${integration.appId}/integrations/${integration.id}/orders/${id}`,
        );
    };
    const baseColumns = [
      columnHelper.accessor("name", {
        id: "name",
        cell: (info) => info.getValue(),
        header: t("translation:name"),
        meta: {
          clickable: false,
        },
      }),
      columnHelper.accessor("directionType", {
        id: "directionType",
        cell: (info) => info.getValue(),
        header: t("translation:direction_type"),
        meta: {
          clickable: false,
        },
      }),
      columnHelper.accessor("id", {
        id: "actions",
        header: "",
        cell: (info) => (
          <button
            onClick={handleClick(info.getValue())}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
          >
            <ArrowCircleRight color="green" />
          </button>
        ),
        meta: {
          clickable: false,
        },
      }),
    ];

    return baseColumns;
  }, [columnHelper, integration.appId, integration.id, navigate, t]);
  return (
    <>
      <InnerControls handleSearch={handleSearch} />
      <Table
        rows={orders}
        tableColumns={columns}
        length={ordersCount}
        sort={sort}
        setSort={setSort}
        loading={isFetching}
        emptyText="No orders found"
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
      />
    </>
  );
}
